import React, { useState, useEffect } from 'react';

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
  isLoading?: boolean;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false
}) => {
  const [organizationName, setOrganizationName] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Validate organization name
  useEffect(() => {
    const trimmedName = organizationName.trim();
    setIsValid(trimmedName.length >= 2 && trimmedName.length <= 200);
  }, [organizationName]);

  // Handle modal animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimating(true);
      setOrganizationName('');
      setIsValid(false);
      // Small delay to allow the modal to appear before fade in
      setTimeout(() => {
        setIsAnimating(false);
      }, 10);
    } else if (isVisible) {
      setIsAnimating(true);
      // Delay to allow fade out animation before hiding
      setTimeout(() => {
        setIsVisible(false);
        setIsAnimating(false);
      }, 150);
    }
  }, [isOpen, isVisible]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid && !isLoading) {
      onConfirm(organizationName.trim());
    }
  };

  const handleClose = () => {
    if (!isLoading && !isAnimating) {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto transition-opacity duration-150 ${
      isAnimating && isOpen ? 'opacity-0' : 'opacity-100'
    } ${isAnimating && !isOpen ? 'opacity-0' : 'opacity-100'}`}>
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className={`fixed inset-0 bg-gray-500 transition-opacity duration-150 ${
            isAnimating && isOpen ? 'bg-opacity-0' : 'bg-opacity-75'
          } ${isAnimating && !isOpen ? 'bg-opacity-0' : 'bg-opacity-75'}`}
          onClick={handleClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all duration-150 sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${
          isAnimating && isOpen ? 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95' : 'opacity-100 translate-y-0 sm:scale-100'
        } ${isAnimating && !isOpen ? 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95' : 'opacity-100 translate-y-0 sm:scale-100'}`}>
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-white px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Create New Organization
              </h3>
            </div>

            {/* Content */}
            <div className="bg-white px-6 py-4">
              <div className="mb-4">
                <label 
                  htmlFor="organizationName" 
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="organizationName"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  minLength={2}
                  maxLength={200}
                  required
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter organization name"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be between 2 and 200 characters
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isValid || isLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                    Creating...
                  </>
                ) : (
                  'Create Organization'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganizationModal;